import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";

export const RATING = {
  low: ["Faible", "Low", "bg-emerald-100 text-emerald-900"], medium: ["Modéré", "Medium", "bg-amber-100 text-amber-900"],
  high: ["Élevé", "High", "bg-orange-200 text-orange-900"], critical: ["Critique", "Critical", "bg-rose-200 text-rose-900"],
};
export const rate = (s, th) => (!s ? null : s >= th.critical ? "critical" : s >= th.high ? "high" : s >= th.medium ? "medium" : "low");

export const RatingBadge = ({ r, score }) => {
  const { lang } = useI18n();
  if (!r) return <span className="text-xs text-slate-400">—</span>;
  return <span data-testid={`rating-${r}`} className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold ${RATING[r][2]}`}>{score} · {lang === "fr" ? RATING[r][0] : RATING[r][1]}</span>;
};

export default function RiskMatrix({ audit, refreshKey }) {
  const { tx } = useI18n();
  const [scope, setScope] = useState("approved");
  const [residual, setResidual] = useState(false);
  const [m, setM] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/audits/${audit.id}/risk-matrix`, { params: { scope, residual } }).then((r) => { if (live) setM(r.data); });
    return () => { live = false; };
  }, [audit.id, scope, residual, refreshKey]);
  if (!m) return null;
  const total = Object.values(m.cells).reduce((a, c) => a + c.length, 0);
  return (
    <section className="card-surface p-4" data-testid="risk-matrix">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="font-display font-semibold text-[#153A63]">{tx("Matrice des risques 5×5", "5×5 risk matrix")} <span className="font-mono text-xs text-slate-500">n = {total}</span></div>
        <select data-testid="matrix-scope" className="nselect ml-auto" value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="approved">{tx("Constats approuvés", "Approved findings")}</option><option value="all">{tx("Tous (hors rejetés) — provisoire", "All (excl. rejected) — provisional")}</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" data-testid="matrix-residual" checked={residual} onChange={(e) => setResidual(e.target.checked)} />{tx("Risque résiduel", "Residual risk")}</label>
      </div>
      <div className="flex gap-2">
        <div className="flex items-center"><span className="label-xs [writing-mode:vertical-rl] rotate-180">{tx("Impact", "Impact")} →</span></div>
        <div className="flex-1">
          <div className="grid grid-cols-[24px_repeat(5,1fr)] gap-1">
            {[5, 4, 3, 2, 1].map((i) => [
              <div key={`l${i}`} className="text-xs font-mono flex items-center justify-center text-slate-500">{i}</div>,
              ...[1, 2, 3, 4, 5].map((l) => {
                const cell = m.cells[`${i}-${l}`] || [];
                const r = rate(i * l, m.thresholds);
                return (
                  <div key={`${i}-${l}`} data-testid={`matrix-cell-${i}-${l}`} className={`min-h-[52px] rounded p-1.5 text-[11px] ${RATING[r][2]} ${cell.length ? "ring-2 ring-[#153A63]/40" : "opacity-70"}`}>
                    <div className="font-mono opacity-60">{i * l}</div>
                    <div className="font-mono font-semibold leading-tight">{cell.map((c) => c.code).join(" ")}</div>
                  </div>
                );
              }),
            ])}
            <div />{[1, 2, 3, 4, 5].map((l) => <div key={l} className="text-xs font-mono text-center text-slate-500">{l}</div>)}
          </div>
          <div className="label-xs text-center mt-1">{tx("Probabilité", "Likelihood")} →</div>
        </div>
      </div>
      <div className="text-xs text-slate-500 mt-2">{tx("Seuils", "Thresholds")}: {tx("modéré", "medium")} ≥ {m.thresholds.medium} · {tx("élevé", "high")} ≥ {m.thresholds.high} · {tx("critique", "critical")} ≥ {m.thresholds.critical}{m.unrated > 0 && ` · ${m.unrated} ${tx("non cotés", "unrated")}`}</div>
    </section>
  );
}
